import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCustomNavigation } from "hooks/useCustomNavigation";
import { CSSProperties, useMemo } from "react";
import { authService } from "services/authService";
import {useLocaleState, useTranslate} from "react-admin";
import {useTheme} from "../../hooks/useTheme";
import {useDeviceDetach} from "../../hooks/useDeviceDetach";
import {Theme} from "../../components/CustomThemeProvider/CustomThemeProvider";
import LoadingButton from "@mui/lab/LoadingButton";
import Link from "@mui/material/Link";
import {environment, Environment} from "../../environment";

export const Settings = () => {
  const t = useTranslate();

  const {theme, setTheme} = useTheme();

  const { isMobile } = useDeviceDetach();

  const { navigate } = useCustomNavigation();

  const [locale, setLocale] = useLocaleState();

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (formData: any) => {
      return authService.logout();
    },
    onSuccess: (data) => {
      queryClient.clear();
      queryClient.setQueryData(["auth", "me"], null);
      navigate("/login");
    },
    onError: (error) => {
      console.warn(error);
    },
  });

  const content = useMemo(
    () => (
      <Box sx={{ paddingX: "15px" }}>
        <Typography
          variant="subtitle2"
          sx={{ marginY: "10px", marginX: "15px" }}
        >
          {t("pages.settings.appearance.label")}
        </Typography>
        <Paper sx={{ padding: "15px" }}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="select-theme-label">
              {t("pages.settings.appearance.theme")}
            </InputLabel>
            <Select
              labelId="select-theme-label"
              value={theme}
              label={t("pages.settings.appearance.theme")}
              onChange={(e) => setTheme(e.target.value as Theme)}
              fullWidth
            >
              <MenuItem value="auto">{t("pages.settings.appearance.options.theme.dark")}</MenuItem>
              <MenuItem value="dark">{t("pages.settings.appearance.options.theme.dark")}</MenuItem>
              <MenuItem value="light">{t("pages.settings.appearance.options.theme.light")}</MenuItem>
            </Select>
          </FormControl>
        </Paper>
        <Typography
          variant="subtitle2"
          sx={{ marginY: "10px", marginX: "15px" }}
        >
          {t("pages.settings.localization.label")}
        </Typography>
        <Paper sx={{ padding: "15px" }}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="select-language-label">
              {t("pages.settings.localization.language")}
            </InputLabel>
            <Select
              labelId="select-language-label"
              value={locale}
              label={t("pages.settings.localization.language")}
              onChange={(e) => {
                setLocale(e.target.value);
              }}
              fullWidth
            >
              <MenuItem value="en">{t("locales.en")}</MenuItem>
              <MenuItem value="ru">{t("locales.ru")}</MenuItem>
            </Select>
          </FormControl>
        </Paper>
        <Typography
            variant="subtitle2"
            sx={{ marginY: "10px", marginX: "15px" }}
        >
          {t("pages.settings.cache.label")}
        </Typography>
        <Paper sx={{ padding: "15px" }}>
          <Link href={`${environment.useSsl ? "https" : "http"}://${environment.apiDomain}/api/v1/cache/download-cache`} target="_blank">
            <Button
                variant="contained"
            >
              {t("pages.settings.cache.download")}
            </Button>
          </Link>
        </Paper>
        <Typography
          variant="subtitle2"
          sx={{ marginY: "10px", marginX: "15px" }}
        >
          {t("pages.settings.authorization.label")}
        </Typography>
        <Paper sx={{ padding: "15px" }}>
          <LoadingButton
            variant="contained"
            loading={mutation.isPending}
            onClick={mutation.mutate}
          >
            {t("ra.auth.logout")}
          </LoadingButton>
        </Paper>
      </Box>
    ),
    [
      locale,
      setLocale,
      mutation.isPending,
      mutation.mutate,
      setTheme,
      t,
      theme,
    ]
  );

  return (
    <Box sx={styles.container}>
      {!isMobile && (
        <Typography variant="h6" sx={{ marginBottom: "15px", marginX: "15px" }}>
          {t("pages.settings.label")}
        </Typography>
      )}
      {content}
    </Box>
  );
};

const styles: { [key: string]: CSSProperties } = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    paddingTop: "15px",
  },
  contentWrapper: {
    padding: "15px",
  },

  formContainer: {
    display: "flex",
    flexDirection: "column",
    padding: "15px",
    boxSizing: "border-box",
    width: "100%",
  },

  descriptionText: {
    margin: "5px 0 25px 0",
  },

  buttonsContainer: {
    display: "flex",
    flexDirection: "row",
    marginTop: "20px",
    justifyContent: "space-between",
    alignItems: "center",
  },
};
